import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useAuth } from "@/context/auth";
import { useBranch } from "@/context/branch";
import { formatCurrency } from "@/lib/currency";
import { readJsonArrayOrEmpty } from "@/lib/http";
import { sortSalesNewestFirst } from "@/lib/sales-order";
import { DashboardMoneyToggle, useDashboardMoneyVisibility } from "./dashboard-money-visibility";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ShoppingCart, Package, TrendingUp, BarChart3,
  RefreshCcw, ArrowRight, AlertTriangle, Banknote, Receipt, Tag,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function StatCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string; sub?: string; icon: React.ElementType; color: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function ReadyToWearDashboard() {
  const { user, store } = useAuth();
  const { activeBranchId, activeBranch } = useBranch();
  const branchQuery = activeBranchId ? `?branchId=${activeBranchId}` : "";
  const isOwner = user?.role === "admin" || user?.role === "superadmin";
  const isManager = user?.role === "manager" || isOwner;
  const { moneyVisible, toggleMoneyVisibility, maskMoney } = useDashboardMoneyVisibility();

  const [stats, setStats] = useState({
    todayRevenue: 0, todayTx: 0,
    todayCash: 0, todayPOS: 0,
    totalProducts: 0, lowStock: 0, outOfStock: 0,
    returnsMonth: 0,
  });
  const [stockItems, setStockItems] = useState<any[]>([]);
  const [recentSales, setRecentSales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const lagosDate = (value: Date | string) => new Date(value).toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" });
        const today = lagosDate(new Date());
        const currentMonth = today.slice(0, 7);
        const [salesRes, productsRes, returnsRes] = await Promise.allSettled([
          fetch(`${BASE}/api/sales${branchQuery}`, { credentials: "include", cache: "no-store" }),
          fetch(`${BASE}/api/products${branchQuery}`, { credentials: "include" }),
          fetch(`${BASE}/api/returns${branchQuery}`, { credentials: "include" }),
        ]);
        const sales = salesRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(salesRes.value) : [];
        const products = productsRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(productsRes.value) : [];
        const returns = returnsRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(returnsRes.value) : [];

        const newestSales = sortSalesNewestFirst(sales);
        const todaySales = newestSales.filter((s: any) => lagosDate(s.createdAt) === today);
        const monthReturns = (returns as any[]).filter((r: any) => lagosDate(r.createdAt).startsWith(currentMonth));
        const activeProducts = products as any[];
        const low = activeProducts.filter((p: any) => p.stock > 0 && p.stock <= (p.reorderLevel ?? 5));
        const out = activeProducts.filter((p: any) => p.stock === 0);
        const todayCash = todaySales.filter((s: any) => (s.paymentMethod || "cash").toLowerCase() === "cash").reduce((a: number, s: any) => a + Number(s.totalAmount ?? s.total ?? 0), 0);
        const todayPOS = todaySales.filter((s: any) => (s.paymentMethod || "").toLowerCase() !== "cash").reduce((a: number, s: any) => a + Number(s.totalAmount ?? s.total ?? 0), 0);

        setStats({
          todayRevenue: todaySales.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0),
          todayTx: todaySales.length,
          todayCash, todayPOS,
          totalProducts: activeProducts.length,
          lowStock: low.length,
          outOfStock: out.length,
          returnsMonth: monthReturns.length,
        });
        setStockItems(activeProducts.sort((a: any, b: any) => a.stock - b.stock).slice(0, 8));
        setRecentSales(todaySales.slice(0, 6));
      } finally { setLoading(false); }
    };
    load();
  }, [branchQuery]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">👔</span>
            <h1 className="text-2xl font-bold">{store?.name || "My Store"}</h1>
            <Badge className="bg-orange-100 text-orange-700 border-orange-200">Ready-to-Wear</Badge>
            <Badge variant="outline" className="capitalize">{user?.role}</Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            Imported Clothing · Adult & Kids Dashboard · {activeBranch?.name ?? "All Branches"} · {new Date().toLocaleDateString("en-NG", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DashboardMoneyToggle visible={moneyVisible} onToggle={toggleMoneyVisibility} />
          <Link href="/returns"><Button variant="outline" size="sm" className="gap-1.5"><RefreshCcw className="w-3.5 h-3.5" />Exchanges</Button></Link>
          <Link href="/sales/new"><Button size="sm" className="gap-1.5 bg-orange-600 hover:bg-orange-700"><ShoppingCart className="w-3.5 h-3.5" />New Sale</Button></Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Today's Sales" value={maskMoney(formatCurrency(stats.todayRevenue))} icon={TrendingUp} color="bg-orange-100 text-orange-600" sub={`${stats.todayTx} piece${stats.todayTx !== 1 ? "s" : ""} / transaction${stats.todayTx !== 1 ? "s" : ""} sold`} />
        <StatCard label="Cash Collected" value={maskMoney(formatCurrency(stats.todayCash))} icon={Banknote} color="bg-green-100 text-green-600" sub={stats.todayPOS > 0 ? `+${maskMoney(formatCurrency(stats.todayPOS))} transfer/POS` : "Cash only today"} />
        <StatCard
          label="Clothing Pieces"
          value={String(stats.totalProducts)}
          icon={stats.outOfStock > 0 ? AlertTriangle : Package}
          color={stats.outOfStock > 0 ? "bg-red-100 text-red-600" : stats.lowStock > 0 ? "bg-amber-100 text-amber-600" : "bg-violet-100 text-violet-600"}
          sub={stats.outOfStock > 0 ? `${stats.outOfStock} sold out — reorder` : stats.lowStock > 0 ? `${stats.lowStock} running low` : "Adult & kids in stock"}
        />
        <StatCard label="Exchanges (Month)" value={String(stats.returnsMonth)} icon={RefreshCcw} color="bg-blue-100 text-blue-600" sub="returns & swaps" />
      </div>

      {/* Out of stock alert */}
      {stats.outOfStock > 0 && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800 flex-1">
            <span className="font-semibold">{stats.outOfStock} clothing piece{stats.outOfStock !== 1 ? "s" : ""} sold out</span> — reorder from your supplier before next market day.
          </p>
          <Link href="/inventory"><Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100 text-xs">View Stock</Button></Link>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Quick Sale",        href: "/sales/new",  icon: ShoppingCart, color: "bg-orange-50 border-orange-200 hover:bg-orange-100" },
            { label: "Clothing Stock",    href: "/inventory",  icon: Tag,          color: "bg-blue-50 border-blue-200 hover:bg-blue-100"     },
            { label: "Returns/Exchanges", href: "/returns",    icon: RefreshCcw,   color: "bg-amber-50 border-amber-200 hover:bg-amber-100"  },
            { label: "Daily Expenses",    href: "/expenses",   icon: Receipt,      color: "bg-purple-50 border-purple-200 hover:bg-purple-100", mgr: true },
          ].filter((a: any) => !a.mgr || isManager).map(a => (
            <Link key={a.label} href={a.href}>
              <div className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors text-slate-800 ${a.color}`}>
                <a.icon className="w-5 h-5 text-slate-700" />
                <span className="text-sm font-semibold">{a.label}</span>
                <ArrowRight className="w-3.5 h-3.5 ml-auto text-slate-500" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Stock List — sorted low stock first */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2"><Package className="w-4 h-4 text-orange-500" />Adult & Kids Clothing Stock</h3>
            <Link href="/inventory"><span className="text-xs text-primary hover:underline cursor-pointer">Manage →</span></Link>
          </div>
          {stockItems.length === 0 ? (
            <div className="text-center py-6">
              <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No clothing added yet — add adult or kids pieces</p>
              <Link href="/inventory?add=1"><Button size="sm" className="mt-3 bg-orange-600 hover:bg-orange-700">Add Clothing Item</Button></Link>
            </div>
          ) : (
            <div className="space-y-2">
              {stockItems.map((p: any) => {
                const isOut = p.stock === 0;
                const isLow = !isOut && p.stock <= (p.reorderLevel ?? 5);
                return (
                  <div key={p.id} className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium truncate">{p.name}</span>
                        {isOut && <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] px-1.5 py-0">Out</Badge>}
                        {isLow && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0">Low</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">{formatCurrency(Number(p.price))}</div>
                    </div>
                    <div className="w-20">
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${isOut ? "bg-red-500" : isLow ? "bg-amber-500" : "bg-emerald-500"}`}
                          style={{ width: `${Math.min(100, (p.stock / Math.max(p.stock, 20)) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <span className={`text-xs font-semibold w-12 text-right ${isOut ? "text-red-600" : isLow ? "text-amber-600" : "text-emerald-600"}`}>
                      {p.stock} pcs
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Today's Transactions */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2"><Banknote className="w-4 h-4 text-green-500" />Today's Transactions</h3>
            <Link href="/sales"><span className="text-xs text-primary hover:underline cursor-pointer">All sales →</span></Link>
          </div>
          {recentSales.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No sales recorded today yet</p>
              <Link href="/sales/new"><Button size="sm" className="mt-3 bg-orange-600 hover:bg-orange-700">Record First Sale</Button></Link>
            </div>
          ) : (
            <div className="space-y-2">
              {recentSales.map((s: any) => {
                const isCash = (s.paymentMethod || "cash").toLowerCase() === "cash";
                return (
                  <div key={s.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div>
                      <div className="text-sm font-medium">{s.customerName ?? "Walk-in Customer"}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${isCash ? "text-green-700 border-green-300" : "text-blue-700 border-blue-300"}`}>
                          {isCash ? "Cash" : s.paymentMethod || "POS"}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{new Date(s.createdAt).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    </div>
                    <div className="text-sm font-semibold">{maskMoney(formatCurrency(Number(s.totalAmount ?? s.total ?? 0)))}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isOwner && (
        <div className="bg-muted/40 border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-orange-500" />Owner Analytics</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Profit & Loss",    href: "/accounting/profit-loss" },
              { label: "Cash Flow",         href: "/accounting/cash-flow" },
              { label: "Expense Reports",   href: "/accounting/expense-reports" },
              { label: "Staff Performance", href: "/people/staff-performance" },
            ].map(l => (
              <Link key={l.label} href={l.href}>
                <Button variant="outline" size="sm" className="w-full text-xs">{l.label}</Button>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
